/**
 * What to say when the microphone does not produce words.
 *
 * There are three different reasons and they were all reported with one
 * sentence: "the recording could not be understood". So a friend of Silvia's
 * whose phone had simply refused the microphone was told that her voice was
 * unclear — the app blaming the person for its own missing permission. She
 * tried again, of course, and got the same answer.
 *
 * The reasons need different words because they need different actions:
 *
 *   not-allowed  the phone refused the microphone   → give permission
 *   network      the request never arrived          → try again, check signal
 *   unknown      it arrived and came back empty     → speak again, or type
 *
 * The AI allowance case is handled separately by each screen, because only
 * some of them may offer a way to lift it.
 */
import { isNativeApp } from './platform';

const MSG = {
  it: {
    denied:  'MeMo non può usare il microfono. Dai il permesso dalle impostazioni del telefono e riprova.',
    deniedBrowser: "Il browser non dà a MeMo il microfono. Tocca l'icona a sinistra dell'indirizzo, poi Autorizzazioni → Microfono, e riprova.",
    network: 'Non sono riuscita a inviare la registrazione. Controlla la connessione e riprova.',
    unknown: 'Non ho capito la registrazione. Riprova a parlare, oppure scrivi.',
  },
  en: {
    denied:  "MeMo can't use the microphone. Allow it in your phone's settings and try again.",
    deniedBrowser: "Your browser is not giving MeMo the microphone. Tap the icon to the left of the address, then Permissions → Microphone, and try again.",
    network: "Couldn't send the recording. Check your connection and try again.",
    unknown: "Couldn't make out the recording. Try speaking again, or type it.",
  },
  es: {
    denied:  'MeMo no puede usar el micrófono. Concede el permiso en los ajustes del teléfono y vuelve a intentarlo.',
    deniedBrowser: "El navegador no le da el micrófono a MeMo. Toca el icono a la izquierda de la dirección, luego Permisos → Micrófono, y vuelve a intentarlo.",
    network: 'No he podido enviar la grabación. Comprueba la conexión e inténtalo de nuevo.',
    unknown: 'No he entendido la grabación. Habla otra vez o escríbelo.',
  },
  de: {
    denied:  'MeMo darf das Mikrofon nicht nutzen. Erlaube es in den Telefoneinstellungen und versuche es erneut.',
    deniedBrowser: "Der Browser gibt MeMo das Mikrofon nicht. Tippe auf das Symbol links neben der Adresse, dann Berechtigungen → Mikrofon, und versuche es erneut.",
    network: 'Die Aufnahme konnte nicht gesendet werden. Prüfe die Verbindung und versuche es erneut.',
    unknown: 'Die Aufnahme war nicht zu verstehen. Sprich noch einmal oder tippe es ein.',
  },
  fr: {
    denied:  "MeMo ne peut pas utiliser le micro. Autorisez-le dans les réglages du téléphone et réessayez.",
    deniedBrowser: "Le navigateur ne donne pas le micro à MeMo. Touchez l'icône à gauche de l'adresse, puis Autorisations → Microphone, et réessayez.",
    network: "L'enregistrement n'a pas pu être envoyé. Vérifiez la connexion et réessayez.",
    unknown: "L'enregistrement n'a pas été compris. Reparlez, ou écrivez-le.",
  },
  nl: {
    denied:  'MeMo mag de microfoon niet gebruiken. Geef toestemming in de telefooninstellingen en probeer opnieuw.',
    deniedBrowser: "De browser geeft MeMo de microfoon niet. Tik op het pictogram links van het adres, dan Rechten → Microfoon, en probeer opnieuw.",
    network: 'De opname kon niet worden verstuurd. Controleer je verbinding en probeer opnieuw.',
    unknown: 'De opname was niet te verstaan. Spreek opnieuw, of typ het.',
  },
  pt: {
    denied:  'O MeMo não pode usar o microfone. Dá permissão nas definições do telemóvel e tenta de novo.',
    deniedBrowser: "O navegador não dá o microfone ao MeMo. Toca no ícone à esquerda do endereço, depois Permissões → Microfone, e tenta de novo.",
    network: 'Não consegui enviar a gravação. Verifica a ligação e tenta de novo.',
    unknown: 'Não percebi a gravação. Fala outra vez, ou escreve.',
  },
  ru: {
    denied:  'MeMo не может использовать микрофон. Разрешите доступ в настройках телефона и попробуйте снова.',
    deniedBrowser: "Браузер не даёт MeMo микрофон. Нажмите значок слева от адреса, затем Разрешения → Микрофон, и попробуйте снова.",
    network: 'Не удалось отправить запись. Проверьте соединение и попробуйте снова.',
    unknown: 'Не удалось разобрать запись. Скажите ещё раз или напишите.',
  },
  pl: {
    denied:  'MeMo nie może użyć mikrofonu. Zezwól na to w ustawieniach telefonu i spróbuj ponownie.',
    deniedBrowser: "Przeglądarka nie daje MeMo mikrofonu. Dotknij ikony po lewej stronie adresu, potem Uprawnienia → Mikrofon, i spróbuj ponownie.",
    network: 'Nie udało się wysłać nagrania. Sprawdź połączenie i spróbuj ponownie.',
    unknown: 'Nie zrozumiałam nagrania. Powiedz jeszcze raz albo napisz.',
  },
  ro: {
    denied:  'MeMo nu poate folosi microfonul. Dă permisiunea din setările telefonului și încearcă din nou.',
    deniedBrowser: "Browserul nu îi dă microfonul lui MeMo. Atinge pictograma din stânga adresei, apoi Permisiuni → Microfon, și încearcă din nou.",
    network: 'Nu am putut trimite înregistrarea. Verifică conexiunea și încearcă din nou.',
    unknown: 'Nu am înțeles înregistrarea. Mai spune o dată, sau scrie.',
  },
  zh: {
    denied:  'MeMo 无法使用麦克风。请在手机设置中允许，然后重试。',
    deniedBrowser: "浏览器没有把麦克风给 MeMo。点按地址左侧的图标，然后「权限」→「麦克风」，再重试。",
    network: '录音未能发送。请检查网络后重试。',
    unknown: '没有听清这段录音。请再说一次，或者直接输入。',
  },
  ja: {
    denied:  'MeMo がマイクを使えません。端末の設定で許可してからもう一度お試しください。',
    deniedBrowser: "ブラウザが MeMo にマイクを渡していません。アドレスの左のアイコンをタップし、「権限」→「マイク」を許可してから、もう一度お試しください。",
    network: '録音を送信できませんでした。通信状態を確認してもう一度お試しください。',
    unknown: '録音を聞き取れませんでした。もう一度話すか、入力してください。',
  },
  hi: {
    denied:  'MeMo माइक्रोफ़ोन का उपयोग नहीं कर सकता। फ़ोन की सेटिंग में अनुमति दें और फिर कोशिश करें।',
    deniedBrowser: "ब्राउज़र MeMo को माइक्रोफ़ोन नहीं दे रहा। पते के बाईं ओर के आइकन पर टैप करें, फिर अनुमतियाँ → माइक्रोफ़ोन, और फिर कोशिश करें।",
    network: 'रिकॉर्डिंग भेजी नहीं जा सकी। कनेक्शन जाँचकर फिर कोशिश करें।',
    unknown: 'रिकॉर्डिंग समझ नहीं आई। दोबारा बोलें, या टाइप करें।',
  },
  ko: {
    denied:  'MeMo가 마이크를 사용할 수 없습니다. 휴대폰 설정에서 허용한 뒤 다시 시도하세요.',
    deniedBrowser: "브라우저가 MeMo에 마이크를 주지 않습니다. 주소 왼쪽 아이콘을 누른 뒤 권한 → 마이크를 허용하고 다시 시도하세요.",
    network: '녹음을 보내지 못했습니다. 연결을 확인하고 다시 시도하세요.',
    unknown: '녹음을 알아듣지 못했습니다. 다시 말하거나 직접 입력하세요.',
  },
  ar: {
    denied:  'لا يستطيع MeMo استخدام الميكروفون. امنح الإذن من إعدادات الهاتف ثم أعد المحاولة.',
    deniedBrowser: "المتصفح لا يمنح MeMo الميكروفون. اضغط على الأيقونة يسار العنوان، ثم الأذونات ← الميكروفون، وأعد المحاولة.",
    network: 'تعذّر إرسال التسجيل. تحقّق من الاتصال وأعد المحاولة.',
    unknown: 'لم أفهم التسجيل. تحدّث مرة أخرى، أو اكتبه.',
  },
};

/**
 * The sentence for a given useWhisper error, in the reader's language.
 * Returns null for 'ai-limit', which each screen words for itself.
 */
export function micErrorText(error, language) {
  if (!error || error === 'ai-limit') return null;
  const base = String(language || 'it').split('-')[0];
  const t = MSG[base] || MSG.en;
  // Which sentence depends on where the person is: inside the installed app
  // there is no browser to open the settings of, and in a browser there is no
  // app entry in the phone's settings to find. Sending somebody to the wrong
  // one is the same as saying nothing.
  if (error === 'not-allowed') return isNativeApp() ? t.denied : t.deniedBrowser;
  if (String(error).startsWith('network')) return t.network;
  return t.unknown;
}

export default micErrorText;
